import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api, { requestsAPI, documentsAPI, customerAPI } from '../../services/api';
import ActivityTimeline from '../../components/ActivityTimeline';
import PhotosTab from '../../components/PhotosTab';
import { formatDate } from '../../utils/formatters';

// Components
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';
import SuccessAlert from '../../components/SuccessAlert';
import StatusBadge from '../../components/StatusBadge';
import RequestBillingManager from '../../components/RequestBillingManager';
import RequestItemsSection from '../../components/RequestItemsSection';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';

const getReceivingServiceLabels = (instructions = {}) => {
  const labels = [];
  if (instructions.require_serial_numbers_on_receive) labels.push('Record serial numbers on receipt');
  if (instructions.require_mac_addresses_on_receive) labels.push('Record MAC addresses on receipt');
  if (instructions.relabel_on_receive) labels.push('Relabel units on receipt');
  return labels;
};

const formatShippingMethod = (method, fallback = 'N/A') => {
  const labels = {
    courier: 'Courier',
    freight: 'Freight',
    pickup: 'Pickup'
  };
  return labels[method] || fallback;
};

const RequestDetail = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Clear any cached data for this request when component mounts
  useEffect(() => {
    queryClient.removeQueries(['request', id]);
    queryClient.invalidateQueries(['request', id]);
  }, [id, queryClient]);

  useEffect(() => {
    const handleDocumentClick = () => setShowActionsMenu(false);
    if (showActionsMenu) {
      document.addEventListener('click', handleDocumentClick);
    }
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [showActionsMenu]);
  
  // Fetch request details
  const { 
    data: request, 
    isLoading, 
    error,
    refetch 
  } = useQuery(
    ['request', id], 
    () => requestsAPI.getRequest(id),
    {
      retry: 2,
      enabled: !!id,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      staleTime: 0,
    }
  );
  
  // Cancel request mutation
  const cancelMutation = useMutation(
    () => requestsAPI.cancelRequest(id),
    {
      onSuccess: () => {
        toast.success('Request cancelled successfully');
        setShowCancelConfirm(false);
        refetch();
        queryClient.invalidateQueries(['requests']);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to cancel request');
        setShowCancelConfirm(false);
      }
    }
  );

  // Handle status update
  const handleStatusUpdate = (newStatus) => {
    setStatusUpdateSuccess(true);
    refetch();
    setTimeout(() => setStatusUpdateSuccess(false), 3000);
  };
  
  // Handle invoice created
  const handleInvoiceCreated = (invoice) => {
    setInvoiceCreated(true);
    refetch();
    setTimeout(() => setInvoiceCreated(false), 3000);
  };

  // Parse cross-dock metadata
  const parseCrossDockMetadata = (specialInstructions) => {
    try {
      return JSON.parse(specialInstructions);
    } catch {
      return {};
    }
  };
  
  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error.message || 'Error loading request'} />;
  if (!request) return <ErrorAlert message="Request not found or access denied" />;
  
  // Format address if it exists
  const formatAddress = (address) => {
    if (!address) return 'N/A';
    
    return (
      <>
        <div>{address.street || address.street_address}</div>
        <div>{address.city}, {address.state || address.state_province} {address.postalCode || address.postal_code}</div>
        <div>{address.country}</div>
      </>
    );
  };

  const isRequestType = (type) => request.requestType === type;
  const crossDockData = isRequestType('crossdock') ? parseCrossDockMetadata(request.specialInstructions) : {};
  const receivingServiceLabels = getReceivingServiceLabels(
    request.receivingInstructions || request.receiving_instructions || {}
  );
  const createdDate = request.createdAt || request.created_at;
  const scheduledDate = request.scheduledDate || request.scheduled_date || request.expected_arrival_date || request.expected_arrival;
  const showItemsInOverview = true;
  const hasShipmentGroups = isRequestType('ship') && Array.isArray(request.shipmentGroups) && request.shipmentGroups.length > 0;
  const requestShipments = Array.isArray(request.Shipments) && request.Shipments.length > 0
    ? request.Shipments
    : (request.Shipment ? [request.Shipment] : []);
  const hasShipment = requestShipments.length > 0 && (isRequestType('ship') || isRequestType('pick_pack') || isRequestType('kit_build'));
  const detailsTabLabel = isRequestType('inbound_manifest') ? 'Manifest Overview' : 'Request Details';
  const detailsCardTitle = isRequestType('inbound_manifest')
    ? 'Manifest Overview'
    : isRequestType('crossdock')
      ? 'Cross-Dock Overview'
      : 'Request Information';
  const detailsCardSubtitle = isRequestType('inbound_manifest')
    ? 'Overview of your inbound manifest and expected items.'
    : 'Details about your request.';
  const editableRequestStatuses = ['draft', 'pending', 'pending_approval', 'approved', 'pending_receive', 'in_progress'];
  const canEditRequest = request && editableRequestStatuses.includes(request.status);
  const hasAvailableActions = !!canEditRequest;
  const requestsBreadcrumbLink = isRequestType('ship')
    ? '/customer/requests?type=ship'
    : '/customer/requests?type=incoming,crossdock,inbound_manifest';
  const requestsBreadcrumbLabel = isRequestType('ship') ? 'Outbound' : 'Inbound';

  const renderItemsContent = () => {
    if (isRequestType('crossdock')) {
      return request.CrossDockPallets && request.CrossDockPallets.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pallet Label
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commodity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dimensions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Weight
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {request.CrossDockPallets.map((pallet) => (
                <tr key={pallet.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {pallet.pallet_label}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {pallet.commodity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {pallet.length}" × {pallet.width}" × {pallet.height}"
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {pallet.gross_weight} lbs
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {pallet.pallet_quantity || 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      pallet.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      pallet.status === 'received' ? 'bg-green-100 text-green-800' :
                      pallet.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {pallet.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500">
          No pallets configured for this cross-dock request.
        </div>
      );
    }

    return <RequestItemsSection request={request} isCustomer={true} embedded={true} />;
  };
  
  return (
    <div className="px-4 py-8 sm:px-6 lg:px-8 max-w-4xl mx-auto">
      {statusUpdateSuccess && (
        <SuccessAlert message="Request status updated successfully" />
      )}
      
      {invoiceCreated && (
        <SuccessAlert message="Invoice created successfully" />
      )}

      <div className="mb-4 flex items-center text-sm text-gray-500">
        <Link to="/customer" className="hover:text-gray-700">Dashboard</Link>
        <FiChevronRight className="mx-2 h-4 w-4 text-gray-400" />
        <Link to={requestsBreadcrumbLink} className="hover:text-gray-700">{requestsBreadcrumbLabel}</Link>
        <FiChevronRight className="mx-2 h-4 w-4 text-gray-400" />
        <span className="text-gray-900">{request?.requestNumber ? request.requestNumber.split('-').slice(0, 2).join('-') : 'Request Detail'}</span>
      </div>
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isRequestType('crossdock') ? 'Cross-Dock Request' : 'Request Details'} {request?.requestNumber ? request.requestNumber.split('-').slice(0, 2).join('-') : ''}
        </h1>
        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowActionsMenu((prev) => !prev);
            }}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Actions
            <FiChevronDown className="ml-2 h-4 w-4" />
          </button>
          {showActionsMenu && (
            <div
              className="absolute right-0 z-10 mt-2 w-48 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {canEditRequest && (
                <Link
                  to={`/customer/requests/${id}/edit`}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowActionsMenu(false)}
                >
                  Edit Request
                </Link>
              )}
              {canEditRequest && (
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    setShowCancelConfirm(true);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  {cancelMutation.isLoading ? 'Cancelling...' : 'Cancel Request'}
                </button>
              )}
              {!hasAvailableActions && (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No actions available
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCancelConfirm(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Cancel Request
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to cancel this request? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  disabled={cancelMutation.isLoading}
                  onClick={() => cancelMutation.mutate()}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                >
                  {cancelMutation.isLoading ? 'Cancelling...' : 'Yes, Cancel Request'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCancelConfirm(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="sm:hidden">
          <select
            id="tabs"
            name="tabs"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
          >
            <option value="details">Details</option>
            {(isRequestType('ship') || isRequestType('crossdock')) && <option value="shipping">Shipping</option>}
            {hasShipment && <option value="shipment">Shipment</option>}
            {hasShipmentGroups && <option value="shipment_groups">Shipment Groups</option>}
            <option value="documents">Documents</option>
            <option value="billing">Billing</option>
            <option value="timeline">Timeline</option>
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`${
                  activeTab === 'details'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {detailsTabLabel}
              </button>
              {(isRequestType('ship') || isRequestType('crossdock')) && (
                <button
                  onClick={() => setActiveTab('shipping')}
                  className={`${
                    activeTab === 'shipping'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Shipping
                </button>
              )}
              {hasShipment && (
                <button
                  onClick={() => setActiveTab('shipment')}
                  className={`${
                    activeTab === 'shipment'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Shipment
                </button>
              )}
              {hasShipmentGroups && (
                <button
                  onClick={() => setActiveTab('shipment_groups')}
                  className={`${
                    activeTab === 'shipment_groups'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Shipment Groups
                </button>
              )}
              <button
                onClick={() => setActiveTab('billing')}
                className={`${
                  activeTab === 'billing'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Billing & Costs
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`${
                  activeTab === 'documents'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Documents
              </button>
              <button
                onClick={() => setActiveTab('timeline')}
                className={`${
                  activeTab === 'timeline'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Timeline
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Details Tab */}
      {activeTab === 'details' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {detailsCardTitle}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                {detailsCardSubtitle}
              </p>
            </div>
            <StatusBadge status={request.status} requestType={request.requestType} />
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Request Type</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {isRequestType('incoming') && 'Incoming Inventory'}
                  {isRequestType('inbound_manifest') && 'Inbound Manifest'}
                  {isRequestType('ship') && 'Ship Products'}
                  {isRequestType('pick_pack') && 'Pick & Pack'}
                  {isRequestType('kit_build') && 'Kit Building'}
                  {isRequestType('crossdock') && 'Cross-Dock'}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Request Number</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {request.requestNumber || 'N/A'}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  {isRequestType('inbound_manifest') ? 'Created' : 'Request Date'}
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {formatDate(createdDate)}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  {isRequestType('inbound_manifest') ? 'Expected Arrival' : 'Scheduled Date'}
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {formatDate(scheduledDate)}
                </dd>
              </div>
              {(isRequestType('incoming') || isRequestType('inbound_manifest')) && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    {isRequestType('inbound_manifest') ? 'Receiving Services' : 'Receiving Instructions'}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {receivingServiceLabels.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {receivingServiceLabels.map((label) => (
                          <span key={label} className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                            {label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      'No special receiving services requested'
                    )}
                  </dd>
                </div>
              )}

              {/* Cross-dock specific fields */}
              {isRequestType('crossdock') && (
                <>
                  {crossDockData.sourceType && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Source Type</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {crossDockData.sourceType === 'existing' ? 'Existing Warehouse' : 'External Source'}
                      </dd>
                    </div>
                  )}
                  {crossDockData.shippingArrangement && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Shipping Arrangement</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {crossDockData.shippingArrangement === 'customer' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Customer Arranges Shipping
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            We Arrange Shipping
                          </span>
                        )}
                      </dd>
                    </div>
                  )}
                  {request.CrossDockPallets && request.CrossDockPallets.length > 0 && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Total Pallets</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {request.CrossDockPallets.reduce((sum, p) => sum + (p.pallet_quantity || 1), 0)} pallets
                      </dd>
                    </div>
                  )}
                </>
              )}

              {/* Ship request specific fields */}
              {isRequestType('ship') && (
                <>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Destination Address</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {formatAddress(request.destinationAddress)}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Shipping Method</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {formatShippingMethod(request.shippingMethod)}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Special Services</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {(request.liftgate_required || request.liftgateRequired) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                          Liftgate Required
                        </span>
                      )}
                      {(request.white_glove || request.whiteGloveService) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-2">
                          White Glove Service
                        </span>
                      )}
                      {request.inside_delivery && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Inside Delivery
                        </span>
                      )}
                      {!(request.liftgate_required || request.liftgateRequired)
                        && !(request.white_glove || request.whiteGloveService)
                        && !request.inside_delivery
                        && 'None'}
                    </dd>
                  </div>
                </>
              )}

              {request.trackingNumber && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Tracking Number</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {request.trackingNumber}
                  </dd>
                </div>
              )}

              <div className={`${request.trackingNumber ? 'bg-gray-50' : 'bg-white'} px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}>
                <dt className="text-sm font-medium text-gray-500">Special Instructions</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {isRequestType('crossdock') ? 
                    (crossDockData.specialInstructions || 'None') : 
                    (request.specialInstructions || 'None')
                  }
                </dd>
              </div>
              
              {/* Kit-specific fields */}
              {isRequestType('kit_build') && request.kitName && (
                <>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Kit Name</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {request.kitName}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Kit Quantity</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {request.kitQuantity}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          {showItemsInOverview && (
            <div className="border-t border-gray-200">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {isRequestType('crossdock') ? 'Pallets' : 'Request Items'}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  {isRequestType('crossdock')
                    ? 'Pallets included in this cross-dock request.'
                    : 'Items included in this request.'}
                </p>
              </div>
              <div className="border-t border-gray-200 pb-2">
                {renderItemsContent()}
              </div>
            </div>
          )}

        </div>
      )}

      {/* Shipping Tab */}
      {activeTab === 'shipping' && (isRequestType('ship') || isRequestType('crossdock')) && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Shipping Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {isRequestType('crossdock') ? 'Cross-dock shipping details and tracking information.' : 'Shipping details and tracking information.'}
            </p>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              {isRequestType('crossdock') && crossDockData.shippingArrangement && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Shipping Arrangement</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {crossDockData.shippingArrangement === 'customer' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Customer Arranges Shipping
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        We Arrange Shipping
                      </span>
                    )}
                  </dd>
                </div>
              )}

              {request.destinationAddress && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Destination Address</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatAddress(request.destinationAddress)}
                  </dd>
                </div>
              )}

              {request.shippingMethod && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Shipping Method</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatShippingMethod(request.shippingMethod)}
                  </dd>
                </div>
              )}

              {request.trackingNumber && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Tracking Number</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {request.trackingNumber}
                  </dd>
                </div>
              )}

              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Special Instructions</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {isRequestType('crossdock') ? 
                    (crossDockData.specialInstructions || '-') : 
                    (request.specialInstructions || '-')
                  }
                </dd>
              </div>
            </dl>
          </div>

        </div>
      )}

      {/* Shipment Tab (read-only tracking/status view) */}
      {activeTab === 'shipment' && hasShipment && (
        <div className="space-y-4">
          {requestShipments.map((shipment, index) => (
            <div key={shipment.id || index} className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {requestShipments.length > 1 ? `Shipment ${index + 1}` : 'Shipment Details'}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Tracking and status information for this shipment.
                </p>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        shipment.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        shipment.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                        shipment.status === 'label_purchased' ? 'bg-indigo-100 text-indigo-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {(shipment.status || 'pending').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </dd>
                  </div>
                  {shipment.carrier && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Carrier</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {shipment.carrier}
                      </dd>
                    </div>
                  )}
                  {(shipment.service || shipment.service_level) && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Service</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {shipment.service || shipment.service_level}
                      </dd>
                    </div>
                  )}
                  {shipment.tracking_number && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Tracking Number</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {shipment.tracking_url || shipment.public_tracking_url ? (
                          <a
                            href={shipment.tracking_url || shipment.public_tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800 underline"
                          >
                            {shipment.tracking_number}
                          </a>
                        ) : (
                          shipment.tracking_number
                        )}
                      </dd>
                    </div>
                  )}
                  {shipment.label_url && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Shipping Label</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <a
                          href={shipment.label_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
                        >
                          Download Label
                        </a>
                      </dd>
                    </div>
                  )}
                  {(shipment.estimated_delivery || shipment.estimated_delivery_date) && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Estimated Delivery</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {formatDate(shipment.estimated_delivery || shipment.estimated_delivery_date)}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Shipment Groups Tab */}
      {activeTab === 'shipment_groups' && hasShipmentGroups && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Shipment Groups</h3>
              <p className="mt-1 text-sm text-gray-500">
                Per-box routing labels for this shipment. Each group generates one sticker per box at print time.
              </p>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const html = await requestsAPI.getShipmentLabels(request.id);
                  const win = window.open('', '_blank');
                  if (win) {
                    win.document.write(html);
                    win.document.close();
                  }
                } catch (err) {
                  alert(err.response?.data || 'Failed to load label sheet');
                }
              }}
              className="inline-flex items-center rounded-md border border-brand-navy bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy/90"
            >
              View Labels
            </button>
          </div>
          <div className="border-t border-gray-200">
            <ul className="divide-y divide-gray-200">
              {request.shipmentGroups.map((group, idx) => (
                <li key={idx} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        {group.project_code && (
                          <span className="text-xs font-semibold uppercase tracking-wide text-brand-gold">
                            {group.project_code}
                          </span>
                        )}
                        {group.project_name && (
                          <span className="text-sm text-gray-500">{group.project_name}</span>
                        )}
                      </div>
                      <div className="mt-1 text-base font-semibold text-brand-navy">
                        {group.room_location || '[no room specified]'}
                      </div>
                      {group.recipient_name && (
                        <div className="mt-1 text-sm text-gray-700">Attn: {group.recipient_name}</div>
                      )}
                      {group.recipient_notes && (
                        <div className="mt-1 text-sm text-gray-500">{group.recipient_notes}</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-brand-navy">{group.box_count ?? 1}</div>
                      <div className="text-xs text-gray-500">box{(group.box_count ?? 1) === 1 ? '' : 'es'}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {isRequestType('crossdock') ? 'Cross-Dock Billing & Costs' : 'Billing & Cost Information'}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {isRequestType('crossdock') ?
                'Automatic billing calculation for cross-dock services' :
                'View costs and invoices for this request'
              }
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            {isRequestType('crossdock') ? (
              // The cross-dock summary endpoint is admin-only (the whole
              // cross-dock router is, deliberately), so this component
              // always 403'd for customers. Invoices still appear on the
              // customer's Invoices page; a tenant-scoped summary
              // endpoint is the future path if the breakdown is needed.
              <p className="text-sm text-gray-500">
                Cross-dock charges are invoiced when processing completes. See your Invoices page for issued invoices.
              </p>
            ) : (
              <RequestBillingManager
                request={request} 
                isAdmin={false}
                onInvoiceCreated={handleInvoiceCreated} 
              />
            )}
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && request && (
        <>
          {isRequestType('inbound_manifest') && (
            <DocumentsTab requestId={request.id} />
          )}
          <div className={isRequestType('inbound_manifest') ? 'mt-6' : ''}>
            <PhotosTab requestId={request.id} />
          </div>
        </>
      )}

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <ActivityTimeline
          fetchFn={(params) => customerAPI.getEntityTimeline('Request', id, params)}
          entityType="Request"
          entityId={id}
          limit={25}
          title={null}
        />
      )}
    </div>
  );
};

// Documents Tab Component
const DocumentsTab = ({ requestId }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const queryClient = useQueryClient();

  // Fetch documents
  const { data: documentsData, isLoading, error, refetch } = useQuery(
    ['documents', requestId],
    () => documentsAPI.getDocuments(requestId),
    {
      enabled: !!requestId,
      staleTime: 30000
    }
  );

  // Upload mutation
  const uploadMutation = useMutation(
    ({ file, documentType, description }) => 
      documentsAPI.uploadDocument(requestId, file, documentType, description),
    {
      onSuccess: () => {
        setUploadSuccess('Document uploaded successfully!');
        setUploadError(null);
        refetch();
        // Clear form
        document.getElementById('file-upload').value = '';
        document.getElementById('document-type').value = 'other';
        document.getElementById('description').value = '';
      },
      onError: (error) => {
        setUploadError(error.response?.data?.message || 'Failed to upload document');
        setUploadSuccess(null);
      },
      onSettled: () => {
        setUploading(false);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (documentId) => documentsAPI.deleteDocument(requestId, documentId),
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        console.error('Delete error:', error);
      }
    }
  );

  const handleFileUpload = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const file = formData.get('file');
    const documentType = formData.get('documentType');
    const description = formData.get('description');

    if (!file) {
      setUploadError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    
    uploadMutation.mutate({ file, documentType, description });
  };

  const handleDelete = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      deleteMutation.mutate(documentId);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      bol: 'Bill of Lading',
      invoice: 'Invoice',
      packing_list: 'Packing List',
      photo: 'Photo',
      other: 'Other'
    };
    return labels[type] || type;
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="Failed to load documents" />;

  const documents = documentsData?.data || [];

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Documents
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Upload and manage documents for this inbound manifest (BOLs, photos, etc.)
        </p>
      </div>
      
      {/* Upload Form */}
      <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
        <form onSubmit={handleFileUpload} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700">
                File
              </label>
              <input
                id="file-upload"
                name="file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
                className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                required
              />
            </div>
            
            <div>
              <label htmlFor="document-type" className="block text-sm font-medium text-gray-700">
                Document Type
              </label>
              <select
                id="document-type"
                name="documentType"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="other">Other</option>
                <option value="bol">Bill of Lading</option>
                <option value="invoice">Invoice</option>
                <option value="packing_list">Packing List</option>
                <option value="photo">Photo</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description (Optional)
              </label>
              <input
                id="description"
                name="description"
                type="text"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Brief description..."
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </form>

        {uploadError && (
          <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="text-sm text-red-700">{uploadError}</div>
          </div>
        )}

        {uploadSuccess && (
          <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-4">
            <div className="text-sm text-green-700">{uploadSuccess}</div>
          </div>
        )}
      </div>

      {/* Documents List */}
      <div className="border-t border-gray-200">
        {documents.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <div className="text-sm text-gray-500">No documents uploaded yet.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    File Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uploaded
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((document) => (
                  <tr key={document.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {document.original_name}
                      {document.description && (
                        <div className="text-xs text-gray-500 mt-1">{document.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {getDocumentTypeLabel(document.document_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatFileSize(document.file_size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {document.created_at ? new Date(document.created_at).toLocaleDateString() : 
                       document.uploaded_at ? new Date(document.uploaded_at).toLocaleDateString() : 
                       'Unknown'}
                      <div className="text-xs text-gray-400">
                        by {document.UploadedBy?.name || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={async () => {
                          try {
                            const urlResponse = await documentsAPI.getDocumentUrl(requestId, document.id);
                            window.open(urlResponse.url, '_blank', 'noopener,noreferrer');
                          } catch (error) {
                            console.error('Error getting document URL:', error);
                            alert('Failed to open document. Please try again.');
                          }
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(document.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={deleteMutation.isLoading}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestDetail; 
